import { fromEvent, take } from 'rxjs';
import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, ViewChild, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { ESelectedEntityType } from '@common/types/layout.type';

import { type WebWidgetModel, type DenormalizedZeroBlockEntity } from '@web/types';

import { AddZeroWidgetComponent } from '@web/layout/content/page-content/components/add-zero-widget';
import { SingleHoverDirective } from '@web/layout/content/page-content/directives/single-hover.directive';
import { ZeroSectionComponent } from '../../sections/zero-section/zero-section.component';
import { AddButtonsComponent } from '../../add-buttons/add-buttons.component';
import { ResizableAddButtonsComponent } from '../../resizable-add-buttons/resizable-add-buttons.component';
import { ZeroBlockControlsComponent } from '../../controls/zero-block-controls/zero-block-controls.component';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';

import { BaseBlockComponent } from '../base-block.abstract';
import { BlockShapeDividers } from '../block-shape-dividers/block-shape-dividers.component';
import {
    atDevice,
    backgroundFilterVar,
    backgroundLayerVar,
    backgroundScrollEffectMode,
    backgroundVideoAtDevice,
    blockHeightVar,
    blockWidthVar,
    borderSideVar,
    zeroBlockBgColorVar,
    zeroBlockBgImageVar,
} from '@sites/entity-core';
import { environment } from '@web-env/environment';
import type { BlockZeroForm } from '@sites/entity-signal-form';
import type { Device } from '@sites/entity-core';
import { CSS_RESOLVERS } from '@web/core/constants/css-resolvers.const';

/**
 * The package is framework-free and each app has its own CDN base, so the colour
 * and image resolvers are bound here rather than inside the shared builder.
 */
/** How many guide lines the canvas draws at each breakpoint. */
const COLUMN_LINES = { desktop: 12, tablet: 8, mobile: 4 } as const;

@Component({
    selector: 'web-zero-block',
    templateUrl: './zero-block.component.html',
    styleUrls: ['./zero-block.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AsyncPipe,
        TranslatePipe,
        TooltipModule,
        SingleHoverDirective,
        BlockShapeDividers,
        ZeroSectionComponent,
        AddButtonsComponent,
        ResizableAddButtonsComponent,
        ZeroBlockControlsComponent,
    ],
})
export class ZeroBlockComponent
    extends BaseBlockComponent<DenormalizedZeroBlockEntity>
    implements OnInit, AfterViewInit
{
    @ViewChild('zeroWrapper') zeroWrapper: ElementRef<HTMLDivElement>;
    @ViewChild('addWidgetBtn') addWidgetBtn: ElementRef<HTMLDivElement>;

    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly contentFacade = inject(ContentFacade);

    /**
     * Styling is read from the signal form the base creates, not from the entity
     * input. Each settings group is its own signal, so editing a height no longer
     * recomputes the background — which with a single bag it did, for every
     * property at once.
     */
    private get zeroForm(): BlockZeroForm {
        return this.form as BlockZeroForm;
    }

    // One signal per settings group, each a single FieldTree node.
    private readonly heightValue = computed(() => this.zeroForm?.height().value());
    private readonly borderValue = computed(() => this.zeroForm?.border().value());
    private readonly backgroundValue = computed(() => this.zeroForm?.background().value());

    // The canvas has no gating class of its own in the editor: it is never
    // pinned, hiding shows a placeholder instead of dropping it, and the 480px
    // down-scale is a published-page behaviour.

    /**
     * Which scroll effect the background layer is under. Parallax also swaps the
     * whole background DOM, so it is a template branch as well as a class.
     */
    private readonly scrollEffect = computed(() => backgroundScrollEffectMode(this.backgroundValue() as never));

    public readonly isParallax = computed<boolean>(() => this.scrollEffect() === 'parallax');
    public readonly isFixedBg = computed<boolean>(() => this.scrollEffect() === 'fixed');

    // One custom property per signal: an edit rewrites the declarations it
    // touches rather than the whole bag.
    public readonly maxWidth = computed(() => blockWidthVar(this.zeroForm?.width().value()));
    public readonly blockHeight = computed(() => blockHeightVar(this.heightValue() as never));
    public readonly blockHeightTablet = computed(() => blockHeightVar(this.heightValue() as never, 'tablet'));
    public readonly blockHeightMobile = computed(() => blockHeightVar(this.heightValue() as never, 'mobile'));

    public readonly bgColor = computed(() => zeroBlockBgColorVar(this.backgroundValue() as never, CSS_RESOLVERS));
    public readonly bgImage = computed(() => zeroBlockBgImageVar(this.backgroundValue() as never, CSS_RESOLVERS));
    public readonly bgPosition = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'position'));
    public readonly bgRepeat = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'repeat'));
    public readonly bgSize = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'size'));
    public readonly bgFilter = computed(() => backgroundFilterVar(this.backgroundValue() as never));
    public readonly borderTop = computed(() => borderSideVar(this.borderValue() as never, 'top'));
    public readonly borderRight = computed(() => borderSideVar(this.borderValue() as never, 'right'));
    public readonly borderBottom = computed(() => borderSideVar(this.borderValue() as never, 'bottom'));
    public readonly borderLeft = computed(() => borderSideVar(this.borderValue() as never, 'left'));

    public readonly bgColorTablet = computed(() =>
        zeroBlockBgColorVar(this.backgroundValue() as never, CSS_RESOLVERS, 'tablet')
    );
    public readonly bgImageTablet = computed(() =>
        zeroBlockBgImageVar(this.backgroundValue() as never, CSS_RESOLVERS, 'tablet')
    );
    public readonly bgPositionTablet = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'position', 'tablet')
    );
    public readonly bgRepeatTablet = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'repeat', 'tablet')
    );
    public readonly bgSizeTablet = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'size', 'tablet')
    );
    public readonly bgFilterTablet = computed(() => backgroundFilterVar(this.backgroundValue() as never, 'tablet'));
    public readonly borderTopTablet = computed(() => borderSideVar(this.borderValue() as never, 'top', 'tablet'));
    public readonly borderRightTablet = computed(() => borderSideVar(this.borderValue() as never, 'right', 'tablet'));
    public readonly borderBottomTablet = computed(() => borderSideVar(this.borderValue() as never, 'bottom', 'tablet'));
    public readonly borderLeftTablet = computed(() => borderSideVar(this.borderValue() as never, 'left', 'tablet'));

    public readonly bgColorMobile = computed(() =>
        zeroBlockBgColorVar(this.backgroundValue() as never, CSS_RESOLVERS, 'mobile')
    );
    public readonly bgImageMobile = computed(() =>
        zeroBlockBgImageVar(this.backgroundValue() as never, CSS_RESOLVERS, 'mobile')
    );
    public readonly bgPositionMobile = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'position', 'mobile')
    );
    public readonly bgRepeatMobile = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'repeat', 'mobile')
    );
    public readonly bgSizeMobile = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'size', 'mobile')
    );
    public readonly bgFilterMobile = computed(() => backgroundFilterVar(this.backgroundValue() as never, 'mobile'));
    public readonly borderTopMobile = computed(() => borderSideVar(this.borderValue() as never, 'top', 'mobile'));
    public readonly borderRightMobile = computed(() => borderSideVar(this.borderValue() as never, 'right', 'mobile'));
    public readonly borderBottomMobile = computed(() => borderSideVar(this.borderValue() as never, 'bottom', 'mobile'));
    public readonly borderLeftMobile = computed(() => borderSideVar(this.borderValue() as never, 'left', 'mobile'));

    /**
     * All three `<video>` elements are rendered when a file is picked; which one
     * shows is a global `.desktop-video` / `.tablet-video` / `.mobile-video` rule.
     */
    public readonly desktopVideoUrl = computed(() => this.videoUrl('desktop'));
    public readonly tabletVideoUrl = computed(() => this.videoUrl('tablet'));
    public readonly mobileVideoUrl = computed(() => this.videoUrl('mobile'));

    private videoUrl(device: Device): string | null {
        const file = backgroundVideoAtDevice(this.backgroundValue() as never, device);

        return file ? `${environment.fileManagerCdnUrl}${file.url}` : null;
    }

    public zeroWrapperTopPosition: number;
    public zeroWrapperBottomPosition: number;

    public ngAfterViewInit(): void {
        const container = document.querySelector('#constructorScrollWrapper') as HTMLDivElement;
        const wrapper = this.zeroWrapper;
        const button = this.addWidgetBtn;

        if (container && wrapper && button) {
            fromEvent(this.zeroWrapper.nativeElement, 'mouseenter')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.updateButtonFixedState(container, wrapper, button);
                });

            fromEvent(container, 'scroll')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.updateButtonFixedState(container, wrapper, button);
                });
        }
    }

    private updateButtonFixedState(
        container: HTMLDivElement,
        wrapper: ElementRef<HTMLDivElement>,
        button: ElementRef<HTMLDivElement>
    ): void {
        const headerHeight = 64;
        const blocksGap = 52;

        this.zeroWrapperTopPosition = wrapper.nativeElement.getBoundingClientRect().top + container.scrollTop;
        this.zeroWrapperBottomPosition = wrapper.nativeElement.getBoundingClientRect().bottom + container.scrollTop;

        const scrollTop = container.scrollTop;
        const topOffset = this.zeroWrapperTopPosition - headerHeight - blocksGap;
        const shouldFix = topOffset < scrollTop && this.zeroWrapperBottomPosition - headerHeight - 150 >= scrollTop;

        button.nativeElement.classList.toggle('top-fixed', shouldFix);
    }

    public addWidget(): void {
        const modalRef: BsModalRef<AddZeroWidgetComponent> = this.modalService.show(AddZeroWidgetComponent, {
            backdrop: false,
            class: 'add-widgets-modal',
            initialState: {},
        });

        modalRef.content.onClose.pipe(take(1)).subscribe((widget: WebWidgetModel) => {
            if (!widget) {
                return;
            }

            const column = this.entity().children?.[0].children?.[0];
            if (!column) {
                console.error('No column found in the block to add the widget to.');
                return;
            }
            this.widgetsFacade.createWidget(column.id, 0, widget.name, { type: 'zero' });
        });
    }

    public selectBlock(): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.zeroBlock, entityId: this.entity().id });
    }

    /**
     * The drag holds one transaction open from pointer-down to pointer-up, the way
     * a zero widget's move and resize do. Nothing is dispatched while it is open —
     * the form's update effect bails out `inTransaction` — so the canvas follows
     * the pointer off the form's own model, and the store and the history batch
     * see the gesture once, at the end, instead of once per `pointermove`.
     *
     * Autosave is paused for the same reason it is during a widget gesture: a save
     * fired mid-drag would persist a height the user is still choosing.
     */
    public onHeightResizeStart(): void {
        this.contentFacade.setAutoSavePaused(true);
        this.zeroForm.beginTransaction();
    }

    public onHeightResizeEnd(): void {
        if (this.zeroForm.inTransaction()) {
            this.zeroForm.commitTransaction();
        }

        this.contentFacade.setAutoSavePaused(false);
    }

    /**
     * Through the form, not the facade — writing the store directly left the
     * form's own model holding the pre-drag height, so the next edit from the
     * settings panel wrote that stale value back.
     *
     * No transaction of its own: during a drag the gesture above owns one, and a
     * call outside a drag is a single edit that should land as a single entry.
     *
     * A canvas always has a height of its own, and the unit never varies.
     */
    public blockHeightChange(value: number): void {
        const height = { value: Math.max(0, Math.round(value)), unit: 'px' as const };
        const devices = this.syncWithDesktop() ? (['desktop', 'tablet', 'mobile'] as const) : [this.device()];

        devices.forEach((device) => this.zeroForm.setHeight(height, device));
    }

    /** The canvas height at the previewed breakpoint — what the resize grip drags. */
    public readonly height = computed<number>(() => {
        const device = this.syncWithDesktop() ? 'desktop' : this.device();

        return (atDevice(this.heightValue() as never, device) as { value?: number } | null)?.value ?? 0;
    });

    /** The guide lines drawn across the canvas — twelve, eight or four per breakpoint. */
    public readonly columnLines = computed<number[]>(() => Array(COLUMN_LINES[this.device()]).fill(0));

    public isSelectedWidgetFromZeroBlock(entityId: string): boolean {
        const widgetsIds = this.entity().children[0].children[0].children.map((widget) => widget.id);

        if (widgetsIds.length) {
            return widgetsIds.includes(entityId);
        }
    }
}
